import type { KLineData } from "klinecharts";
import type { Candle } from "../types";

/**
 * Pure, canvas-free helpers behind the Chart boundary (spec §11, §14). These
 * carry all the logic that decides *what* the chart shows, so it can be tested
 * without a real KLineChart/canvas; Chart.tsx only wires their output into the
 * imperative chart API.
 */

/** Map domain Candles to KLineChart's data shape (millisecond timestamps). */
export function toKLineData(candles: Candle[]): KLineData[] {
  return candles.map((c) => ({
    timestamp: Date.parse(c.time),
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume ?? 0,
  }));
}

/**
 * The candles the chart should display: the Context plus the first `count`
 * Forward candles. `count` is 0 while anonymized and grows as the reveal
 * animation steps through the Forward, reaching `forward.length` when fully
 * revealed.
 */
export function withForward(
  context: Candle[],
  forward: Candle[],
  count: number,
): Candle[] {
  const clamped = Math.max(0, Math.min(count, forward.length));
  return [...context, ...forward.slice(0, clamped)];
}

/** The shortest gap worth animating: below a frame it is not a step at all. */
const MIN_STEP_MS = 16;

/**
 * The longest gap between two Forward candles. Without a ceiling the step is
 * the whole duration budget divided by a possibly tiny Forward, so the
 * Next-Candle Loops (Forward of exactly one candle) spent the entire 600ms
 * budget waiting and then popped the candle in: not an animation, a stall, and
 * the lag players reported after a guess. Capped, a short Forward gets a beat
 * of anticipation and a long one is unchanged (a 20-candle Forward steps every
 * 30ms, well under the cap).
 */
const MAX_STEP_MS = 80;

/**
 * Milliseconds between Forward candles as the reveal steps them in: the total
 * duration budget spread over the Forward, clamped to a range that stays an
 * animation at both ends.
 */
export function forwardStepMs(forwardLength: number, durationMs: number): number {
  if (forwardLength <= 0) return MIN_STEP_MS;
  const even = Math.round(durationMs / forwardLength);
  return Math.min(MAX_STEP_MS, Math.max(MIN_STEP_MS, even));
}

/**
 * Percent move of the latest visible candle's close from the first visible
 * candle's open, the anonymized "% from start" readout shown in place of
 * absolute prices. Returns 0 for an empty series.
 */
export function pctFromStart(candles: Candle[]): number {
  if (candles.length === 0) return 0;
  const start = candles[0].open;
  const current = candles[candles.length - 1].close;
  if (start === 0) return 0;
  return ((current - start) / start) * 100;
}

/**
 * How many decimals the y-axis (and the crosshair bubble) should print for a
 * series at this price scale. KLineChart's default of 2 is right for an index
 * or a metal, but flattens an FX rate: every 6E label came out "1.14", so the
 * axis carried no information at all. Scaling the decimals by magnitude keeps
 * roughly six significant digits across the bank — ES 1198.50, SI 30.125,
 * 6E 1.1425 — instead of padding ES out to 1198.5000.
 */
export function pricePrecision(candles: Candle[]): number {
  let largest = 0;
  for (const c of candles) largest = Math.max(largest, Math.abs(c.high), Math.abs(c.close));
  if (largest >= 100) return 2;
  if (largest >= 10) return 3;
  if (largest >= 1) return 4;
  if (largest >= 0.1) return 5;
  // Sub-0.1 (and the empty-series 0) fall back to the finest step we print.
  return 6;
}

/** Approximate width KLineChart reserves for the y-axis price gutter. */
export const AXIS_RESERVE = 64;
/** Empty strip left on the right so the reveal has room to append into. */
export const RIGHT_OFFSET = 8;

const MIN_BAR_SPACE = 2;
/**
 * KLineChart's own ceiling: `setBarSpace` silently ignores anything above it,
 * which would leave the chart on its *previous* bar space. So the clamp is the
 * library's limit, not a taste choice — capping lower (this was 14) under-fills
 * the pane on short Rounds, and because KLineChart anchors the last bar to the
 * right, the slack shows up as a dead strip on the LEFT (the next-candle Loops,
 * where a Round is ~35 candles against a 1200px pane, were the worst case).
 */
const MAX_BAR_SPACE = 50;

/**
 * The per-candle bar space that fits `totalCandles` into the usable chart
 * width (container width minus the y-axis gutter and the right offset),
 * clamped to KLineChart's accepted range. A non-positive candle count or a
 * container too narrow to have any usable width falls back to the minimum.
 *
 * `axisReserve` is the gutter to leave for the y-axis. It defaults to the
 * revealed axis; a caller fitting an anonymized pane, where the axis is
 * collapsed to zero width, can pass 0 to use that strip for candles.
 */
export function fitBarSpace(
  containerWidth: number,
  totalCandles: number,
  axisReserve: number = AXIS_RESERVE,
): number {
  const usable = containerWidth - axisReserve - RIGHT_OFFSET;
  if (totalCandles <= 0 || usable <= 0) return MIN_BAR_SPACE;
  const space = usable / totalCandles;
  return Math.min(MAX_BAR_SPACE, Math.max(MIN_BAR_SPACE, space));
}
